<template>
    <v-container class="py-12">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span id="title" class="page-title">Passageiros em lote</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', disabled: false, to: '/home', exact: true },
                            { text: 'Passageiros em lote', disabled: false, to: '/passageiros-lote', exact: true },
                            { text: isNew ? 'Nova importação' : 'Detalhes', disabled: true, exact: true },
                        ]"></v-breadcrumbs>
                    </div>
                    <div class="button-group" style="padding-right: 16px;">
                        <v-btn v-if="isNew" id="btnImport" type="button" color="primary" elevation="0" class="mr-2"
                            :loading="importing" :disabled="validating" @click="submit()">Importar</v-btn>
                        <v-btn id="btnBack" type="button" elevation="0" color="primary" outlined
                            to="/passageiros-lote">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- NOVA IMPORTACAO -->
        <v-row v-if="isNew">
            <v-col cols="12">
                <v-card>
                    <v-card-text>
                        <v-form ref="form">
                            <v-row align="center" class="mb-1">
                                <v-col cols="12">
                                    <h3 class="subtitle-1 font-weight-bold mb-1">Suba a planilha com os passageiros</h3>
                                    <div class="d-flex align-center flex-wrap">
                                        <span class="body-2 grey--text mr-3">
                                            Os passageiros serão cadastrados na empresa selecionada.
                                        </span>
                                        <v-btn text small color="primary" @click="dialogFormat = true">
                                            <v-icon left small>mdi-file-table-outline</v-icon>
                                            Ver formato da planilha
                                        </v-btn>
                                    </div>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-autocomplete dense outlined label="Empresa" :items="dataset.customers"
                                        :loading="dataset.customerLoading" :search-input.sync="searchCustomer"
                                        v-model="form.customer_id" item-value="id" item-text="name" cache-items
                                        hide-details="auto" :rules="[$rules.required]"
                                        no-data-text="Nenhuma empresa encontrada"></v-autocomplete>
                                </v-col>

                                <v-col cols="12" md="6">
                                    <v-file-input dense outlined label="Planilha (.xlsx ou .csv)"
                                        accept=".xlsx,.xls,.csv" prepend-icon="" prepend-inner-icon="mdi-paperclip"
                                        v-model="form.file" hide-details="auto" :rules="[$rules.required]"
                                        @change="result = null"></v-file-input>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-radio-group v-model="form.mode" dense hide-details class="mt-0"
                                        label="O que fazer com a planilha" @change="result = null">
                                        <v-radio value="create" label="Cadastrar novos passageiros"></v-radio>
                                        <v-radio value="update"
                                            label="Atualizar existentes e cadastrar os que faltarem"></v-radio>
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12" md="6" v-if="form.mode === 'update'">
                                    <v-radio-group v-model="form.match_by" dense hide-details class="mt-0"
                                        label="Identificar o passageiro existente pela" @change="result = null">
                                        <v-radio value="registration" label="Matrícula"></v-radio>
                                        <v-radio value="name"
                                            label="Nome (quando a matrícula da planilha não é a real)"></v-radio>
                                    </v-radio-group>
                                    <p class="caption grey--text mt-2 mb-0">
                                        Células vazias não apagam o que já está cadastrado. A matrícula (e a senha do
                                        app) dos passageiros existentes não é alterada.
                                    </p>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12">
                                    <v-btn color="primary" outlined :loading="validating"
                                        :disabled="importing" @click="validateFile()">
                                        <v-icon left small>mdi-check-circle-outline</v-icon>
                                        Validar arquivo
                                    </v-btn>
                                    <span class="body-2 grey--text ml-3">
                                        Confira a planilha antes de importar
                                    </span>
                                </v-col>
                            </v-row>
                        </v-form>

                        <!-- RESULTADO DA VALIDACAO -->
                        <div v-if="result" class="mt-6">
                            <v-divider class="mb-4"></v-divider>

                            <h3 class="subtitle-1 font-weight-bold mb-2">Resultado da validação</h3>

                            <div class="mb-3">
                                <v-chip small class="mr-2">{{ result.total }} linha(s)</v-chip>
                                <v-chip small dark color="success" class="mr-2">{{ result.valid }} válida(s)</v-chip>
                                <v-chip small dark color="error" class="mr-2" v-if="result.invalid">{{ result.invalid }} com
                                    erro</v-chip>
                                <template v-if="result.mode === 'update'">
                                    <v-chip small outlined color="primary" class="mr-2">{{ result.to_update }} a
                                        atualizar</v-chip>
                                    <v-chip small outlined color="primary" class="mr-2">{{ result.to_create }} a
                                        cadastrar</v-chip>
                                </template>
                            </div>

                            <v-alert v-if="result.mode === 'update' && result.missing && result.missing.length" dense
                                text type="info" class="body-2">
                                {{ result.missing.length }} passageiro(s) cadastrado(s) nesta empresa não aparecem na
                                planilha e serão mantidos como estão.
                                <v-btn x-small text color="primary" @click="showMissing = !showMissing">
                                    {{ showMissing ? 'Ocultar' : 'Ver lista' }}
                                </v-btn>
                                <div v-if="showMissing" class="missing-list mt-2">
                                    <div v-for="item in result.missing" :key="`missing-${item.id}`">
                                        {{ item.name }} <span class="grey--text">({{ item.registration || 'sem matrícula' }})</span>
                                    </div>
                                </div>
                            </v-alert>

                            <v-alert v-if="result.invalid" dense text type="warning" class="body-2">
                                As linhas com erro não serão importadas. Corrija a planilha e suba novamente — quem já
                                entrou não será duplicado.
                            </v-alert>

                            <v-data-table :headers="validationHeaders" :items="result.rows" dense
                                :items-per-page="10" class="app-table result-table"
                                :footer-props="{ itemsPerPageText: 'Itens por página:' }">
                                <template v-slot:[`item.city`]="{ item }">
                                    {{ item.city || '-' }}
                                    <v-tooltip bottom v-if="item.city_detected">
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" small color="primary" class="ml-1">mdi-auto-fix</v-icon>
                                        </template>
                                        <span>Cidade reconhecida no texto do endereço</span>
                                    </v-tooltip>
                                </template>
                                <template v-slot:[`item.valid`]="{ item }">
                                    <v-chip x-small dark :color="item.valid ? 'success' : 'error'">
                                        {{ item.valid ? 'OK' : 'Erro' }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.message`]="{ item }">
                                    <span v-if="!item.valid" class="error--text">{{ item.message }}</span>
                                    <span v-else-if="result.mode === 'update'">
                                        {{ item.action === 'updated' ? `Atualizar passageiro #${item.passenger_id}` : 'Cadastrar novo' }}
                                    </span>
                                    <span v-else>-</span>
                                </template>
                            </v-data-table>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- DETALHES DE UMA IMPORTACAO -->
        <template v-else>
            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-card-text>
                            <h3 class="subtitle-1 font-weight-bold mb-3">Resultado da importação</h3>

                            <v-row>
                                <v-col cols="12" md="6">
                                    <p class="mb-1"><strong>Arquivo:</strong> {{ dataset.data.file_name }}</p>
                                    <p class="mb-1"><strong>Empresa:</strong>
                                        {{ dataset.data.customer ? dataset.data.customer.name : '-' }}</p>
                                    <p class="mb-1"><strong>Modo:</strong>
                                        {{ dataset.data.mode === 'update'
                                            ? `Atualizar existentes (por ${dataset.data.match_by === 'name' ? 'nome' : 'matrícula'})`
                                            : 'Cadastrar novos' }}</p>
                                    <p class="mb-1"><strong>Status:</strong>
                                        <v-chip x-small dark :color="statusColor(dataset.data.status)">
                                            {{ statusLabel(dataset.data.status) }}
                                        </v-chip>
                                    </p>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <p class="mb-1"><strong>Total de linhas:</strong> {{ dataset.data.total_rows }}</p>
                                    <p class="mb-1"><strong>Importados:</strong> {{ dataset.data.success_rows }}
                                        <span v-if="dataset.data.mode === 'update'" class="grey--text">
                                            ({{ countByAction('updated') }} atualizados, {{ countByAction('created') }} cadastrados)
                                        </span>
                                    </p>
                                    <p class="mb-1"><strong>Com erro:</strong> {{ dataset.data.error_rows }}</p>
                                    <p class="mb-1" v-if="withoutAddressCount">
                                        <strong>Sem endereço:</strong> {{ withoutAddressCount }}
                                        <v-icon color="amber darken-2" small>mdi-map-marker-alert</v-icon>
                                    </p>
                                </v-col>
                            </v-row>

                            <v-alert v-if="dataset.data.message" dense text type="error" class="body-2 mt-2">
                                {{ dataset.data.message }}
                            </v-alert>

                            <v-alert v-if="dataset.data.status === 2 || dataset.data.status === 1" dense text
                                type="info" class="body-2 mt-2">
                                A planilha está sendo processada. Esta tela atualiza sozinha.
                            </v-alert>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-card-text>
                            <h3 class="subtitle-1 font-weight-bold mb-3">Detalhes da importação</h3>

                            <v-data-table :headers="rowHeaders" :items="dataset.data.rows || []" dense
                                :items-per-page="20" class="app-table result-table"
                                no-data-text="Nenhuma linha processada"
                                :footer-props="{ itemsPerPageText: 'Itens por página:' }">
                                <template v-slot:[`item.status`]="{ item }">
                                    <v-chip x-small dark :color="item.status === 1 ? 'success' : 'error'">
                                        {{ item.status !== 1 ? 'Erro' : item.action === 'updated' ? 'Atualizado' : 'Cadastrado' }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.message`]="{ item }">
                                    <span :class="{ 'error--text': item.status === 2 }">{{ item.message || '-' }}</span>
                                </template>
                                <template v-slot:[`item.passenger_id`]="{ item }">
                                    <a v-if="item.passenger_id" :href="`/passageiros/${item.passenger_id}`">
                                        #{{ item.passenger_id }}
                                    </a>
                                    <span v-else>-</span>
                                </template>

                                <template v-slot:[`item.name`]="{ item }">
                                    {{ item.name }}
                                    <v-tooltip bottom v-if="item.status === 1 && !item.city && !item.address">
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" color="amber darken-2" small class="ml-1">
                                                mdi-map-marker-alert
                                            </v-icon>
                                        </template>
                                        <span>Passageiro sem endereço — complete o cadastro para o cálculo de rota
                                            funcionar</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <!-- MODAL FORMATO DA PLANILHA -->
        <v-dialog v-model="dialogFormat" max-width="820px" scrollable>
            <v-card class="pa-3">
                <v-card-title>
                    <h3 class="custom-modal-title text-uppercase black--text font-weight-bold">Formato do arquivo</h3>
                </v-card-title>

                <v-card-text>
                    <p class="body-2">
                        Aceitamos arquivos <strong>.xlsx</strong> ou <strong>.csv</strong>. O sistema localiza a linha
                        de cabeçalho sozinho, então logotipo e linhas em branco no topo da planilha não atrapalham.
                    </p>

                    <p class="body-2 mb-1"><strong>Colunas esperadas</strong></p>

                    <p class="caption grey--text mb-2">
                        A ordem das colunas não importa, e o nome do cabeçalho aceita variações — maiúsculas,
                        minúsculas e acentos são ignorados.
                    </p>

                    <div class="scroll-box">
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th style="width: 150px;">Coluna</th>
                                    <th style="width: 110px;">Obrigatória</th>
                                    <th>Também aceita</th>
                                    <th>Observação</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="col in columnHelp" :key="col.name">
                                    <td class="font-weight-bold">{{ col.name }}</td>
                                    <td>
                                        <v-chip x-small dark :color="col.required ? 'error' : 'grey lighten-1'">
                                            {{ col.required ? 'Sim' : 'Não' }}
                                        </v-chip>
                                    </td>
                                    <td class="grey--text">{{ col.aliases }}</td>
                                    <td class="grey--text">{{ col.note }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </div>

                    <v-btn color="primary" small class="my-4" :loading="downloadingTemplate" @click="downloadTemplate()">
                        <v-icon left small>mdi-download</v-icon>
                        Baixar modelo (.xlsx)
                    </v-btn>

                    <p class="body-2 mb-1"><strong>Exemplo de linhas válidas:</strong></p>

                    <div class="scroll-box sheet-preview">
                        <table class="sheet-table">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Matrícula</th>
                                    <th>Celular</th>
                                    <th>Endereço</th>
                                    <th>Nº</th>
                                    <th>BAIRRO</th>
                                    <th>CIDADE</th>
                                    <th>Centro de Custo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(ex, i) in exampleRows" :key="i">
                                    <td>{{ ex.name }}</td>
                                    <td class="num">{{ ex.registration }}</td>
                                    <td class="num">{{ ex.phone }}</td>
                                    <td>{{ ex.address }}</td>
                                    <td class="num">{{ ex.number }}</td>
                                    <td>{{ ex.district }}</td>
                                    <td>{{ ex.city }}</td>
                                    <td class="num">{{ ex.costCenter }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogFormat = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";

export default {
    name: "PassengerImportDetails",

    data: () => ({
        dataset: {
            data: {},
            customers: [],
            customerLoading: false,
        },

        form: {
            customer_id: null,
            file: null,
            mode: "create",
            match_by: "registration",
        },

        showMissing: false,

        result: null,
        validating: false,
        importing: false,
        dialogFormat: false,
        downloadingTemplate: false,
        searchCustomer: null,
        pollTimer: null,

        validationHeaders: [
            { text: "Linha", value: "row_number", width: 70, align: "center" },
            { text: "Status", value: "valid", width: 90, align: "center" },
            { text: "Nome", value: "name", width: "26%" },
            { text: "Matrícula", value: "registration", width: 100 },
            { text: "Cidade", value: "city", width: "16%" },
            { text: "Mensagem", value: "message", width: "34%", cellClass: "cell-wrap" },
        ],

        rowHeaders: [
            { text: "Linha", value: "row_number", width: 70, align: "center" },
            { text: "Status", value: "status", width: 100, align: "center" },
            { text: "Nome", value: "name", width: "22%" },
            { text: "Matrícula", value: "registration", width: 95 },
            { text: "Celular", value: "phone_number", width: 120 },
            { text: "Cidade", value: "city", width: "13%" },
            { text: "Passageiro", value: "passenger_id", width: 95, align: "center" },
            { text: "Mensagem", value: "message", width: "27%", cellClass: "cell-wrap" },
        ],

        columnHelp: [
            {
                name: "Nome", required: true,
                aliases: "nome completo, passageiro, colaborador, funcionario",
                note: "Nome do passageiro"
            },
            {
                name: "Matrícula", required: true,
                aliases: "registro, registration, chapa",
                note: "Não pode repetir na mesma empresa"
            },
            {
                name: "Celular", required: false,
                aliases: "telefone, fone, phone, contato",
                note: "Com ou sem formatação"
            },
            {
                name: "Endereço", required: false,
                aliases: "logradouro, rua",
                note: "Sem ele o passageiro entra sinalizado, sem localização"
            },
            {
                name: "Nº", required: false,
                aliases: "numero, num, no",
                note: "Somado ao endereço na busca"
            },
            {
                name: "Bairro", required: false,
                aliases: "distrito, district",
                note: "Ajuda a localizar o endereço"
            },
            {
                name: "Cidade", required: false,
                aliases: "municipio, city",
                note: "Sem ela o passageiro entra sinalizado, sem localização"
            },
            {
                name: "Complemento", required: false,
                aliases: "compl",
                note: "Apto, bloco, referência"
            },
            {
                name: "Centro de Custo", required: false,
                aliases: "centro custo, cc, cost center",
                note: "Código ou nome; precisa já existir cadastrado na empresa"
            },
        ],

        exampleRows: [
            {
                name: "ANTONIO PEREIRA DA ROCHA", registration: "4579", phone: "51900000001",
                address: "RUA SANTOS FILHO", number: "412", district: "MISSOES", city: "SOLEDADE",
                costCenter: "7213"
            },
            {
                name: "MARCELO TAVARES LIMA", registration: "7020", phone: "51900000002",
                address: "RUA ALDINO LOUREIRO - APTO 103", number: "95", district: "FONTES", city: "SOLEDADE",
                costCenter: "7213"
            },
            {
                name: "GERALDO MOURA DA CRUZ", registration: "7937", phone: "51900000003",
                address: "RUA FLORENCIO JUNG - PREDIO CINZA", number: "156", district: "EXPEDICIONARIO",
                city: "SOLEDADE", costCenter: "7213"
            },
            {
                name: "SEBASTIAO NUNES BARBOSA", registration: "10046", phone: "51900000004",
                address: "RUA JULIO CARDOSO", number: "308", district: "MISSOES", city: "SOLEDADE",
                costCenter: "7213"
            },
        ],
    }),

    computed: {
        isNew() {
            return this.$route.params.id === "novo" || !this.$route.params.id;
        },

        // Passageiros que entraram mas ficaram sem endereco: precisam de complemento
        // manual, senao o calculo de rota das viagens nao funciona para eles.
        withoutAddressCount() {
            const rows = this.dataset.data.rows || [];

            return rows.filter((r) => r.status === 1 && !r.city && !r.address).length;
        },
    },

    watch: {
        searchCustomer(value) {
            if (value) this.loadCustomers(value);
        },
    },

    created() {
        if (this.isNew) this.loadCustomers();
        else this.load();
    },

    beforeDestroy() {
        this.stopPolling();
    },

    methods: {
        countByAction(action) {
            return (this.dataset.data.rows || []).filter((r) => r.status === 1 && r.action === action).length;
        },

        statusLabel(status) {
            const map = { 1: "Pendente", 2: "Processando", 3: "Concluído", 4: "Erro" };

            return map[status] || "-";
        },

        statusColor(status) {
            if (status === 3) return "success";
            if (status === 4) return "error";
            if (status === 2) return "info";

            return "grey";
        },

        loadCustomers: debounce(async function (filter = "") {
            this.dataset.customerLoading = true;

            try {
                const response = await axios.get("api/v2/customers", {
                    params: { page: 1, limit: 50, search: { name: filter } },
                });

                const content = response.data.content;

                this.dataset.customers = content.data || content || [];

            } catch (error) {
                console.error("Erro ao carregar empresas:", error);
            } finally {
                this.dataset.customerLoading = false;
            }
        }, 400),

        buildFormData() {
            const data = new FormData();

            data.append("file", this.form.file);
            data.append("customer_id", this.form.customer_id);
            data.append("mode", this.form.mode);
            data.append("match_by", this.form.match_by);

            return data;
        },

        async validateFile() {
            if (!this.$refs.form.validate()) return;

            this.validating = true;
            this.result = null;

            try {
                const response = await axios.post("api/v2/passenger-imports/validate", this.buildFormData(), {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                this.result = response.data.content;

                if (this.result.invalid === 0) {
                    this.$dialog.notify.success("Planilha válida! Pode importar.", { position: "top-right", timeout: 4000 });
                }

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 6000 });
            } finally {
                this.validating = false;
            }
        },

        async submit() {
            if (!this.$refs.form.validate()) return;

            this.importing = true;

            try {
                await axios.post("api/v2/passenger-imports", this.buildFormData(), {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                this.$dialog.notify.success("Planilha enviada! O processamento roda em segundo plano.",
                    { position: "top-right", timeout: 5000 });

                this.$router.push("/passageiros-lote");

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 6000 });
            } finally {
                this.importing = false;
            }
        },

        async downloadTemplate() {
            this.downloadingTemplate = true;

            try {
                // O modelo vem pronto do backend: gerado com exceljs, ja com as
                // larguras de coluna e matricula/celular como texto - baixando um CSV
                // o Excel converte o telefone para notacao cientifica.
                const response = await axios.get("api/v2/passenger-imports/template", {
                    responseType: "blob",
                });

                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const link = document.createElement("a");

                link.href = URL.createObjectURL(blob);
                link.download = "modelo-passageiros.xlsx";
                link.click();

                URL.revokeObjectURL(link.href);

            } catch (error) {
                this.$dialog.notify.error("Não foi possível baixar o modelo.", { position: "top-right", timeout: 5000 });
            } finally {
                this.downloadingTemplate = false;
            }
        },

        startPolling() {
            if (this.pollTimer) return;

            this.pollTimer = setInterval(() => this.load(true), 6000);
        },

        stopPolling() {
            if (!this.pollTimer) return;

            clearInterval(this.pollTimer);
            this.pollTimer = null;
        },

        async load(silent = false) {
            try {
                const response = await axios.get(`api/v2/passenger-imports/${this.$route.params.id}`);

                this.dataset.data = response.data.content.data;

                // Enquanto estiver pendente ou processando, mantem atualizando
                if (this.dataset.data.status === 1 || this.dataset.data.status === 2) this.startPolling();
                else this.stopPolling();

            } catch (error) {
                this.stopPolling();

                if (!silent) {
                    this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
                }
            }
        },
    },
};
</script>

<style>
/* Tabelas de resultado: sem table-layout fixed o Vuetify redistribui as colunas
   pelo conteudo e a mensagem de erro fica espremida. O !important e necessario
   porque .app-table (main.scss) tambem usa !important nas celulas. */
.result-table table {
    table-layout: fixed !important;
    width: 100% !important;
}

.result-table th,
.result-table td {
    vertical-align: middle !important;
    padding: 6px 10px !important;
}

.result-table th {
    white-space: nowrap !important;
}

/* Colunas curtas em linha unica, com reticencias se estourar. A celula da
   mensagem tem a classe cell-wrap e fica de fora, podendo quebrar. */
.result-table td:not(.cell-wrap) {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
}

.result-table td.cell-wrap {
    white-space: normal !important;
    line-height: 1.35 !important;
    word-break: break-word;
}
</style>

<style scoped>
.scroll-box {
    overflow-x: auto;
    border: 1px solid rgba(0, 0, 0, .12);
    border-radius: 4px;
    margin-bottom: 8px;
}


/* Reproduz a aparencia da planilha para o operador reconhecer o formato */
.missing-list {
    max-height: 220px;
    overflow-y: auto;
}

.sheet-preview {
    border-color: #217346;
}

.sheet-table {
    border-collapse: collapse;
    width: 100%;
    font-size: 12.5px;
    white-space: nowrap;
}

.sheet-table th,
.sheet-table td {
    border: 1px solid #b7b7b7;
    padding: 3px 8px;
    text-align: left;
}

.sheet-table th {
    background: #f2f2f2;
    font-weight: 700;
}

.sheet-table .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
}
</style>
